import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpEvent, HttpInterceptor, HttpErrorResponse, HttpHandler, HttpRequest, HttpHeaders, HttpResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { StorageService } from '../services/storage.service';
import { AuthService } from '../services/auth.service';
import { BroadcastService } from '../services/broadcast.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor (
    private router: Router,
    private storage: StorageService,
    private authService: AuthService,
    private toastrService: ToastrService,
    private broadcastService: BroadcastService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authToken = this.storage.getUserToken();
    if (!authToken) { return next.handle(req); }

    let authReq;

    if (req.headers.has('Authorization')) {
      authReq = req.clone();
    } else {
      authReq = req.clone({
        setHeaders: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authToken.access_token,
          'X-Project-Flow': environment.manager_type
        }
      });
    }

    return next.handle(authReq)
      .pipe(
        tap(void 0, responseError => {

          if (!(responseError instanceof HttpErrorResponse)) {
            return;
          }

          if (responseError.status === 401 && this.authService.isAuthenticated()) {
            this.broadcastService.sendLogoutMessage();
            this.authService.logout()
              .subscribe(() => {
                this.router.navigate(['/auth/signin']);
              });
          }

          if (responseError.status === 500) {
            this.toastrService.clear();
            this.toastrService.error(
              'Nossa! O servidor encontrou uma condição inesperada e ficou sem saber o que fazer.', 'Erro interno do servidor');
          }

          if (responseError.status === 404) {
            this.toastrService.clear();
            this.toastrService.error(
              'O recurso que você tentou acessar não foi encontrado.', 'Erro 404');
          }

          if (responseError.status === 400) {
            if (responseError.error.errorMessage) {
              return;
            } else if (Object.keys(responseError.error.details).length) {
              this.toastGenerator({
                title: responseError.error.details.title || 'Alguma coisa deu errado!',
                message: responseError.error.details.message || 'Informe ao administrador do sistema.',
                type: responseError.error.details.type || 'error',
              })
            } else {
              let message;

              this.toastrService.clear();
              switch (responseError.error.code) {
                case 1:
                  message = 'Alguns parâmetros não foram enviados ao servidor.';
                  break;
                case 2:
                  message = 'Alguns parâmetros não podem ser atualizados.';
                  break;
                case 3:
                  message = 'Os parâmetros enviados não são válidos. Experimente recarregar a página.';
                  break;
                case 5:
                  message = 'As APIs de terceiros não estão funcionando.';
                  break;
                case 6:
                  message = 'Não foi possível criar o que foi solicitado.';
                  break;
              }

              this.toastrService.error(message || 'Informe ao administrador do sistema.', 'Alguma coisa deu errado!');
            }
          }

          if (responseError.status === 403) {
            let message;
            switch (responseError.error.code) {
              case 4:
                message = 'Apenas super administradores podem executar esta ação!';
                this.router.navigate(['/']);
                break;

            }

            this.toastrService.clear();
            this.toastrService.error(message || 'Informe ao administrador do sistema.', 'Você não tem permissão!');
          }

          if (responseError.url.includes('_bulk')) {
            return;
          }

          if (responseError.status === 0) {
            this.toastrService.clear();
            this.toastrService.error(
              'Não foi possível completar a requisição. Pode ser que o TAQE esteja em manutenção ou você esteja sem conexão.',
              'Servidor inalcançável');
          }
        })
      );
  }

  private toastGenerator({ title, message, type }: { title: string, message: string, type: 'error' | 'info' | 'warning' }): void {
    this.toastrService.clear();
    this.toastrService[type](message, title, {
      enableHtml: true
    });
  }
}
